/**
 * underlay.js — PNG underlay image state module.
 *
 * Exports:
 *   setUnderlay          — store image object and filename
 *   clearUnderlay        — reset all underlay state to defaults
 *   getUnderlayImage     — return the stored HTMLImageElement (or null)
 *   getUnderlayFilename  — return the stored filename string (or null)
 *   hasUnderlay          — return true if an image is loaded
 *   getUnderlayScale     — return the current underlay scale (default 1.0)
 *   setUnderlayScale     — set scale clamped to 0.1–4.0, rounded to 0.1
 *   isUnderlayVisible    — return true if underlay is visible (default true)
 *   setUnderlayVisible   — show or hide the underlay
 *   computeUnderlayDrawParams — pure function: compute draw {x, y, w, h} for renderer
 */

// ---------------------------------------------------------------------------
// Private state
// ---------------------------------------------------------------------------

let _image    = null;
let _filename = null;
let _dataUrl  = null;
let _scale    = 1.0;
let _visible  = true;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIN_SCALE  = 0.1;
const MAX_SCALE  = 4.0;
const SCALE_STEP = 0.02;

// ---------------------------------------------------------------------------
// Setters / mutators
// ---------------------------------------------------------------------------

/**
 * Store the loaded image element and its source filename.
 *
 * @param {HTMLImageElement} image
 * @param {string} filename
 */
export function setUnderlay(image, filename, dataUrl) {
  _image    = image;
  _filename = filename;
  _dataUrl  = dataUrl || _imageToDataUrl(image);
}

/**
 * Reset all underlay state to initial defaults.
 * Call this on "New diagram" or "Remove underlay" actions.
 */
export function clearUnderlay() {
  _image    = null;
  _filename = null;
  _dataUrl  = null;
  _scale    = 1.0;
  _visible  = true;
}

/**
 * Set the underlay display scale.
 * Clamps to [MIN_SCALE, MAX_SCALE] and rounds to nearest SCALE_STEP.
 *
 * @param {number} value
 */
export function setUnderlayScale(value) {
  const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
  _scale = Math.round(clamped / SCALE_STEP) * SCALE_STEP;
}

/**
 * Show or hide the underlay without removing it from state.
 *
 * @param {boolean} visible
 */
export function setUnderlayVisible(visible) {
  _visible = visible;
}

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

/**
 * Return the loaded image element, or null if none is loaded.
 *
 * @returns {HTMLImageElement|null}
 */
export function getUnderlayImage() {
  return _image;
}

/**
 * Return the filename of the loaded underlay image, or null.
 *
 * @returns {string|null}
 */
export function getUnderlayFilename() {
  return _filename;
}

/**
 * Return true if an underlay image is currently loaded.
 *
 * @returns {boolean}
 */
export function hasUnderlay() {
  return _image !== null;
}

/**
 * Return the base64 data URL of the loaded underlay image, or null.
 *
 * @returns {string|null}
 */
export function getUnderlayDataUrl() {
  return _dataUrl;
}

/**
 * Return the current underlay display scale (default 1.0).
 *
 * @returns {number}
 */
export function getUnderlayScale() {
  return _scale;
}

/**
 * Return true if the underlay is currently set to visible.
 *
 * @returns {boolean}
 */
export function isUnderlayVisible() {
  return _visible;
}

// ---------------------------------------------------------------------------
// Pure geometry
// ---------------------------------------------------------------------------

/**
 * Compute the draw rectangle for the underlay image using stretch-to-fill.
 * At scale=1.0 the image exactly fills the bbox pixel dimensions regardless
 * of its natural aspect ratio. The image centers on the bbox center.
 * Note: imgW/imgH are kept in the signature for API compatibility but are
 * not used for sizing.
 *
 * All values are in logical CSS pixels (not device pixels).
 *
 * @param {number} imgW          - natural/source image width in pixels
 * @param {number} imgH          - natural/source image height in pixels
 * @param {{ topLat: number, leftLng: number, bottomLat: number, rightLng: number }} bbox
 *   - geo bounding box (currently maps 1:1 to the canvas)
 * @param {{ width: number, height: number }} logicalSize - canvas logical dimensions
 * @param {number} underlayScale - user scale factor multiplied on top of contained fit
 * @returns {{ x: number, y: number, w: number, h: number }}
 */
// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Convert an HTMLImageElement to a PNG data URL via an offscreen canvas.
 * Returns null if the image has no dimensions (e.g. in test/node env).
 */
function _imageToDataUrl(image) {
  if (!image || !image.naturalWidth || typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width  = image.naturalWidth;
  c.height = image.naturalHeight;
  c.getContext("2d").drawImage(image, 0, 0);
  return c.toDataURL("image/png");
}

export function computeUnderlayDrawParams(imgW, imgH, bbox, logicalSize, underlayScale) {
  const bboxPixelW = logicalSize.width;
  const bboxPixelH = logicalSize.height;
  const bboxCenterX = logicalSize.width / 2;
  const bboxCenterY = logicalSize.height / 2;

  // Stretch to fill: image fills bbox exactly at scale=1.0
  const drawW = bboxPixelW * underlayScale;
  const drawH = bboxPixelH * underlayScale;

  return {
    x: bboxCenterX - drawW / 2,
    y: bboxCenterY - drawH / 2,
    w: drawW,
    h: drawH,
  };
}
